import { useSignal } from "@preact/signals";
import { useEffect, useRef } from "preact/hooks";
import { Button } from "./button";
import { useFocusTrap } from "./use-focus-trap";

/** Minimum crop region size in display pixels */
const MIN_CROP_SIZE = 64;

/** WebP output quality (0-1). Matches server config default. */
const IMAGE_QUALITY = 0.85;

type ImageCropperProps = {
  /** Source image (File or data URL) */
  source: File | string;
  /** Output size in pixels (default 256) */
  outputSize?: number;
  /** Called when crop is confirmed. Return a promise to show upload progress. */
  onCrop: (blob: Blob) => void | Promise<void>;
  /** Called when user cancels */
  onCancel: () => void;
};

type CropRegion = {
  x: number;
  y: number;
  size: number;
};

/**
 * Crop image to square and convert to WebP blob.
 */
async function cropImage(
  source: HTMLImageElement,
  cropRect: CropRegion,
  outputSize: number,
): Promise<Blob> {
  // Create cropped bitmap
  const cropped = await createImageBitmap(
    source,
    cropRect.x,
    cropRect.y,
    cropRect.size,
    cropRect.size,
  );

  // Draw to canvas at output size
  const canvas = new OffscreenCanvas(outputSize, outputSize);
  const ctx = canvas.getContext("2d");
  if (!ctx) {
    throw new Error("Failed to get canvas context");
  }
  ctx.drawImage(cropped, 0, 0, outputSize, outputSize);
  cropped.close();

  // Convert to WebP blob
  return canvas.convertToBlob({
    type: "image/webp",
    quality: IMAGE_QUALITY,
  });
}

/**
 * Modal image cropper component.
 * Shows a draggable square crop region over the source image.
 * Outputs a cropped WebP blob at the specified size.
 */
export function ImageCropper({
  source,
  outputSize = 256,
  onCrop,
  onCancel,
}: ImageCropperProps) {
  const containerRef = useFocusTrap<HTMLDivElement>({
    open: true,
    onEscape: onCancel,
  });

  const imageRef = useRef<HTMLImageElement>(null);
  const cropRegionRef = useRef<HTMLDivElement>(null);

  // Image dimensions (natural and displayed)
  const imageNaturalWidth = useSignal(0);
  const imageNaturalHeight = useSignal(0);
  const imageDisplayWidth = useSignal(0);
  const imageDisplayHeight = useSignal(0);

  // Crop region state (in display coordinates)
  const cropX = useSignal(0);
  const cropY = useSignal(0);
  const cropSize = useSignal(0);

  // Drag state
  const isDragging = useSignal(false);
  const isResizing = useSignal(false);
  const dragStart = useSignal({ x: 0, y: 0, cropX: 0, cropY: 0 });
  const resizeStart = useSignal({ x: 0, y: 0, size: 0 });

  // Processing state (cropping the image)
  const isProcessing = useSignal(false);
  // Upload state (after cropping, waiting for upload)
  const isUploading = useSignal(false);
  const error = useSignal<string | null>(null);
  // Store the cropped blob for retry
  const croppedBlob = useSignal<Blob | null>(null);

  // Source URL for the image
  const sourceUrl = useSignal<string | null>(null);

  // Create object URL if source is a File
  useEffect(() => {
    if (source instanceof File) {
      const url = URL.createObjectURL(source);
      sourceUrl.value = url;
      return () => URL.revokeObjectURL(url);
    }
    sourceUrl.value = source;
  }, [source]);

  // Initialize crop region when image loads
  function handleImageLoad() {
    const img = imageRef.current;
    if (!img) return;

    imageNaturalWidth.value = img.naturalWidth;
    imageNaturalHeight.value = img.naturalHeight;
    imageDisplayWidth.value = img.width;
    imageDisplayHeight.value = img.height;

    // Center the crop region, size it to fit the smaller dimension
    const minDimension = Math.min(img.width, img.height);
    const size = Math.floor(minDimension * 0.8);

    cropSize.value = size;
    cropX.value = Math.floor((img.width - size) / 2);
    cropY.value = Math.floor((img.height - size) / 2);
  }

  // Clamp crop region to image bounds
  function clampCropRegion(x: number, y: number, size: number) {
    const maxX = imageDisplayWidth.value - size;
    const maxY = imageDisplayHeight.value - size;
    return {
      x: Math.max(0, Math.min(x, maxX)),
      y: Math.max(0, Math.min(y, maxY)),
      size: Math.max(
        32,
        Math.min(size, imageDisplayWidth.value, imageDisplayHeight.value),
      ),
    };
  }

  // Clear error state when user adjusts the crop (so they re-crop with new selection)
  function clearErrorState() {
    if (error.value) {
      error.value = null;
      croppedBlob.value = null;
    }
  }

  // Handle drag start on the crop region
  function handleDragStart(e: MouseEvent | TouchEvent) {
    if (isResizing.value) return;
    e.preventDefault();
    clearErrorState();

    const point = "touches" in e ? e.touches[0] : e;
    if (!point) return;
    isDragging.value = true;
    dragStart.value = {
      x: point.clientX,
      y: point.clientY,
      cropX: cropX.value,
      cropY: cropY.value,
    };
  }

  // Handle resize start on the corner handle
  function handleResizeStart(e: MouseEvent | TouchEvent) {
    e.preventDefault();
    e.stopPropagation();
    clearErrorState();

    const point = "touches" in e ? e.touches[0] : e;
    if (!point) return;
    isResizing.value = true;
    resizeStart.value = {
      x: point.clientX,
      y: point.clientY,
      size: cropSize.value,
    };
  }

  // Handle mouse/touch move
  function handleMove(e: MouseEvent | TouchEvent) {
    if (!isDragging.value && !isResizing.value) return;

    const point = "touches" in e ? e.touches[0] : e;
    if (!point) return;

    if (isDragging.value) {
      const deltaX = point.clientX - dragStart.value.x;
      const deltaY = point.clientY - dragStart.value.y;
      const clamped = clampCropRegion(
        dragStart.value.cropX + deltaX,
        dragStart.value.cropY + deltaY,
        cropSize.value,
      );
      cropX.value = clamped.x;
      cropY.value = clamped.y;
    }

    if (isResizing.value) {
      // Resize from bottom-right corner
      const deltaX = point.clientX - resizeStart.value.x;
      const deltaY = point.clientY - resizeStart.value.y;
      const delta = Math.max(deltaX, deltaY);
      const newSize = resizeStart.value.size + delta;

      // Ensure the crop region stays within bounds
      const maxSize = Math.min(
        imageDisplayWidth.value - cropX.value,
        imageDisplayHeight.value - cropY.value,
      );
      const clampedSize = Math.max(MIN_CROP_SIZE, Math.min(newSize, maxSize));
      cropSize.value = clampedSize;
    }
  }

  // Handle mouse/touch end
  function handleEnd() {
    isDragging.value = false;
    isResizing.value = false;
  }

  // Add global event listeners for dragging/resizing
  useEffect(() => {
    function onMouseMove(e: MouseEvent) {
      handleMove(e);
    }
    function onMouseUp() {
      handleEnd();
    }
    function onTouchMove(e: TouchEvent) {
      handleMove(e);
    }
    function onTouchEnd() {
      handleEnd();
    }

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
    document.addEventListener("touchmove", onTouchMove);
    document.addEventListener("touchend", onTouchEnd);

    return () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.removeEventListener("touchmove", onTouchMove);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, []);

  async function performUpload(blob: Blob) {
    isUploading.value = true;
    error.value = null;

    try {
      // onCrop may return a promise (for upload) - await it
      await onCrop(blob);
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Upload failed";
    } finally {
      isUploading.value = false;
    }
  }

  async function handleConfirm() {
    const img = imageRef.current;
    if (!img) return;

    isProcessing.value = true;
    error.value = null;

    try {
      // Convert display coordinates to natural image coordinates
      const scaleX = imageNaturalWidth.value / imageDisplayWidth.value;
      const scaleY = imageNaturalHeight.value / imageDisplayHeight.value;

      const naturalCrop: CropRegion = {
        x: Math.round(cropX.value * scaleX),
        y: Math.round(cropY.value * scaleY),
        size: Math.round(cropSize.value * Math.min(scaleX, scaleY)),
      };

      const blob = await cropImage(img, naturalCrop, outputSize);
      croppedBlob.value = blob;
      isProcessing.value = false;

      await performUpload(blob);
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to crop image";
      isProcessing.value = false;
    }
  }

  function handleRetry() {
    if (croppedBlob.value) {
      performUpload(croppedBlob.value);
    }
  }

  if (!sourceUrl.value) {
    return null;
  }

  return (
    <div
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cropper-title"
      data-brand="slate"
    >
      <div
        ref={containerRef}
        class="relative w-full max-w-lg bg-surface rounded-xl shadow-xl animate-scale-in flex flex-col max-h-[90vh]"
      >
        {/* Header */}
        <div class="px-5 py-4 border-b border-border flex-shrink-0">
          <h3 id="cropper-title" class="text-lg font-semibold text-text">
            Crop Image
          </h3>
        </div>

        {/* Image container with crop overlay */}
        <div class="p-5 overflow-auto flex-1">
          <div
            class={`relative inline-block w-full overflow-hidden rounded-lg bg-black ${error.value ? "ring-2 ring-dashed ring-red-500" : ""}`}
          >
            <img
              ref={imageRef}
              src={sourceUrl.value}
              alt="Preview for cropping"
              onLoad={handleImageLoad}
              class="max-w-full h-auto block mx-auto select-none"
              style={{ maxHeight: "50vh" }}
              draggable={false}
            />

            {/* Dark overlay with crop cutout */}
            {imageDisplayWidth.value > 0 && (
              <div
                class="absolute inset-0 pointer-events-none"
                style={{
                  background: `
                    linear-gradient(to right, rgba(0,0,0,0.5) ${cropX.value}px, transparent ${cropX.value}px),
                    linear-gradient(to left, rgba(0,0,0,0.5) ${imageDisplayWidth.value - cropX.value - cropSize.value}px, transparent ${imageDisplayWidth.value - cropX.value - cropSize.value}px),
                    linear-gradient(to bottom, rgba(0,0,0,0.5) ${cropY.value}px, transparent ${cropY.value}px),
                    linear-gradient(to top, rgba(0,0,0,0.5) ${imageDisplayHeight.value - cropY.value - cropSize.value}px, transparent ${imageDisplayHeight.value - cropY.value - cropSize.value}px)
                  `,
                }}
              />
            )}

            {/* Crop region */}
            {imageDisplayWidth.value > 0 && (
              <div
                ref={cropRegionRef}
                class="absolute border-2 border-white cursor-move"
                style={{
                  left: `${cropX.value}px`,
                  top: `${cropY.value}px`,
                  width: `${cropSize.value}px`,
                  height: `${cropSize.value}px`,
                  boxShadow: "0 0 0 9999px rgba(0,0,0,0.5)",
                }}
                onMouseDown={handleDragStart}
                onTouchStart={handleDragStart}
              >
                {/* Corner resize handle */}
                <div
                  class="absolute -right-2 -bottom-2 w-4 h-4 bg-white rounded-full cursor-se-resize shadow-md"
                  onMouseDown={handleResizeStart}
                  onTouchStart={handleResizeStart}
                />
                {/* Grid lines */}
                <div class="absolute inset-0 grid grid-cols-3 grid-rows-3 pointer-events-none">
                  <div class="border-r border-b border-white/30" />
                  <div class="border-r border-b border-white/30" />
                  <div class="border-b border-white/30" />
                  <div class="border-r border-b border-white/30" />
                  <div class="border-r border-b border-white/30" />
                  <div class="border-b border-white/30" />
                  <div class="border-r border-white/30" />
                  <div class="border-r border-white/30" />
                  <div />
                </div>
              </div>
            )}
          </div>

          {error.value && (
            <p class="text-sm text-red-600 dark:text-red-400 mt-3">
              {error.value}
            </p>
          )}
        </div>

        {/* Footer with buttons */}
        <div class="px-5 py-4 border-t border-border flex-shrink-0 flex justify-end gap-3">
          {error.value && croppedBlob.value ? (
            // Upload failed - show retry option
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={isUploading.value}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleRetry}
                disabled={isUploading.value}
                loading={isUploading.value}
              >
                Retry Upload
              </Button>
            </>
          ) : (
            // Normal crop/upload flow
            <>
              <Button
                type="button"
                variant="secondary"
                onClick={onCancel}
                disabled={isProcessing.value || isUploading.value}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="primary"
                onClick={handleConfirm}
                disabled={isProcessing.value || isUploading.value}
                loading={isProcessing.value || isUploading.value}
              >
                {isUploading.value ? "Uploading..." : "Upload"}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
